import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query'
import { useNotificationContext } from 'shared/context/Notifications'
import { AxiosError } from 'axios'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import { FavouriteProducersResponse } from './types'
import { copyFavouriteProducers, editFavouriteProducers, fetchFavouriteProducers } from './services'

export default function useFavouriteProducers(
   locationUuid: string,
   categoryUuid: string
): UseQueryResult<FavouriteProducersResponse> {
   return useQuery(
      ['favouriteProducers', 'list', locationUuid, categoryUuid],
      () => fetchFavouriteProducers(locationUuid, categoryUuid),
      {
         initialData: {
            producers: {},
         },
         enabled: !!locationUuid && !!categoryUuid,
      }
   )
}

export function useFavouriteProducersMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<
      Result,
      AxiosError<ErrorType> | Error,
      { payload: FavouriteProducersResponse; locationUuid: string; categoryUuid: string },
      unknown
   >(
      ({ payload, locationUuid, categoryUuid }) => {
         return editFavouriteProducers(locationUuid, categoryUuid, payload)
      },
      {
         onSuccess: () => {
            queryClient.invalidateQueries('favouriteProducers')
            addNotification('successSave', 'success')
         },
         onError: (error) => {
            if (error) {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            }
         },
      }
   )
}

export function useCopyFavouriteProducersMutation() {
   const queryClient = useQueryClient()
   const { addNotification } = useNotificationContext()

   return useMutation<Result, AxiosError<ErrorType> | Error, { locationUuid: string; categoryUuid?: string }, unknown>(
      ({ locationUuid, categoryUuid }) => {
         return copyFavouriteProducers(locationUuid, categoryUuid)
      },
      {
         onSuccess: () => {
            queryClient.invalidateQueries('favouriteProducers')
            addNotification('successSave', 'success')
         },
         onError: (error) => {
            if (error) {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            }
         },
      }
   )
}
